/* NEOPINE: 干涉丝带（Interference Ribbons）— 细发光丝组成的带，漂移扭转（2026-10-15）
   ------------------------------------------------------------------------------
   来源：React Bits Pro 的 Interference Ribbons（https://pro.reactbits.dev/docs/components/interference-ribbons）。
   本项目是 Shopify Liquid 主题，没有 React / components.json / 构建步骤 ⇒ `npx shadcn add` 装不了，
   这里按官方文档的 30 个 prop 做等价 WebGL 实现，默认配色改为品牌青（与 neopine-smoke.js 的 TONE_A 同源）。

   用法（配合 snippets/np-ribbons.liquid）：
     <div class="np-ribbons" data-np-ribbons style="--np-rb-...">
       <script type="application/json" class="np-ribbons__cfg">{...}</script>
     </div>
   脚本读 __cfg 里的 JSON 当参数，缺项回落到 DEFAULTS。

   设计要点（每条都对应一个「容易做错」的地方）：
   1. 丝带 = N 条丝横跨带宽排布，沿长度方向扭转。转到「侧对镜头」时所有丝在屏幕上叠成
      **亮点结** —— 这是这个组件最标志性的观感，别把它做成一条均匀的亮带。
   2. **亮部必须保色相**：N 条丝的柔光会在丝带中心线性累加 ⇒ 三通道一起撞顶变成死白，
      品牌青就丢了。两处治理：软肩压缩（单调、渐近 1.0、不动色相）+ 渐进饱和累加
      （已亮像素增益自动变小，保住单根丝的层次而不是糊成一块平光板）。
   3. **性能**：wires 上限 160，着色器里用常量循环上界 + `if (k >= N) break`，
      避免动态循环在部分驱动上被强制展开。
   4. **安全退化**：拿不到 WebGL 就整层不输出（纯装饰，不影响任何内容）；
      用户开「减少动效」时冻结动画但保留静态画面。
   5. **懒初始化**：IntersectionObserver 进视口才建 GL 上下文，离开视口暂停 rAF。
      移动端可再用 `--np-rb-quality` 降采样，控制填充率。
*/
(function () {
  'use strict';

  var MAX_WIRES = 160;

  var DEFAULTS = {
    colors: ['#37C9D8', '#9BEEFF'],   // 品牌青 → 浅青（neopine-smoke.js TONE_A 同源）
    backgroundColor: '#000000',        // 对齐首页 hero 纯黑底
    mode: 'auto',
    wires: 64,
    lineWidth: 1.6,
    ribbonWidth: 0.24,
    amplitude: 0.6,
    waveLength: 1.0,
    angle: 42,
    twist: 1.6,
    curl: 0.25,
    depth: 0.65,
    stripes: 2.2,
    colorFlow: 0.04,
    glow: 0.45,
    intensity: 1.0,
    speed: 0.7,
    spin: 0.35,
    interactive: true,
    hoverPulses: true,
    clickPulse: true,
    pulseSpeed: 1.1,
    pulseStrength: 1.0,
    hoverBoost: 1.0,
    paused: false,
    quality: 1.0
  };

  var VERT = [
    'attribute vec2 aPos;',
    'void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'uniform vec2  u_res;',
    'uniform float u_time;     // 动画时间（paused 时冻结）',
    'uniform float u_ptime;    // 脉冲时间（永远走）',
    'uniform vec3  u_colA;',
    'uniform vec3  u_colB;',
    'uniform vec3  u_bg;',
    'uniform float u_glowMode; // 1=glow(加光) 0=ink(上色)',
    'uniform float u_wires;',
    'uniform float u_lineWidth;',
    'uniform float u_ribbonWidth;',
    'uniform float u_amplitude;',
    'uniform float u_waveLength;',
    'uniform float u_angle;',
    'uniform float u_twist;',
    'uniform float u_curl;',
    'uniform float u_depth;',
    'uniform float u_stripes;',
    'uniform float u_colorFlow;',
    'uniform float u_glow;',
    'uniform float u_intensity;',
    'uniform float u_speed;',
    'uniform float u_spin;',
    'uniform float u_pulseSpeed;',
    'uniform float u_pulseStrength;',
    'uniform float u_hover;',
    'uniform float u_hoverBoost;',
    'uniform vec4  u_pulseA;   // x=along0 y=t0 z=strength w=-1 表示对称双向',
    'uniform vec4  u_pulseB;',
    '',
    'const float PI = 3.14159265359;',
    '',
    'float hash11(float x){ return fract(sin(x * 12.9898) * 43758.5453); }',
    '',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / u_res;',
    '  float asp = u_res.x / max(u_res.y, 1.0);',
    '  vec2 p = (uv - 0.5) * vec2(asp, 1.0);',
    '',
    '  // ---- 旋转到丝带坐标系：along=沿带方向, across=跨带方向 ----',
    '  float a = radians(u_angle);',
    '  float ca = cos(a), sa = sin(a);',
    '  float along  =  p.x * ca + p.y * sa;',
    '  float across = -p.x * sa + p.y * ca;',
    '',
    '  // ---- 中心线：蜿蜒（waveLength / amplitude）----',
    '  float X = along / max(u_waveLength, 0.05);',
    '  float cy = u_amplitude * 0.30 * sin(X * PI * 0.60 + u_time * 0.80);',
    '',
    '  // ---- 扭转相位 ----',
    '  float phi = u_twist * PI * X + u_spin * u_time * 2.0;',
    '  float halfW = max(u_ribbonWidth * 0.5, 1e-4);',
    '  float N = clamp(u_wires, 4.0, ' + MAX_WIRES + '.0);',
    '  float lwPix = (u_lineWidth / max(u_res.y, 1.0)) * 0.5;',
    '  vec3  deepCol = u_colA * 0.30;',
    '',
    '  float core = 0.0;',
    '  vec3  col  = vec3(0.0);',
    '',
    '  for (int k = 0; k < ' + MAX_WIRES + '; k++) {',
    '    if (float(k) >= N) break;',
    '    float c = (N > 1.0) ? (float(k) / (N - 1.0) * 2.0 - 1.0) : 0.0;',
    '',
    '    // curl：截面弯曲 ⇒ 各丝相位错开，成束又散开',
    '    float cphi = phi - u_curl * c * c * 3.2;',
    '    float cw = cos(cphi);',
    '    float sw = sin(cphi);',
    '    float yo = c * halfW * cw;   // 屏幕纵向偏移',
    '    float zo = c * halfW * sw;   // 离屏深度（决定背面变暗变细）',
    '',
    '    float d = across - (cy + yo);',
    '',
    '    // depth：背面的丝变暗变细',
    '    float far  = clamp(-zo / max(halfW, 1e-4), 0.0, 1.0);',
    '    float face = mix(1.0, 0.30, u_depth * far);',
    '    float w    = max(lwPix * face, 1e-5);',
    '    float t2 = d / w;',
    '    float line = exp(-t2 * t2 * 2.5);',
    '',
    '    // glow：丝周围的柔光',
    '    float gw = max(w * 3.5 + u_glow * 10.0 / max(u_res.y, 1.0), 1e-5);',
    '    float g2 = d / gw;',
    '    float halo = exp(-g2 * g2 * 1.6);',
    '',
    '    // 颜色：stripes 跨带循环 + colorFlow 沿丝漂移',
    '    float tc = fract((c * 0.5 + 0.5) * u_stripes + u_colorFlow * u_time * PI * 2.0);',
    '    vec3 sc = mix(u_colA, u_colB, smoothstep(0.0, 1.0, tc));',
    '    // 相邻丝不要读成一条平带 ⇒ 每丝给一点亮度抖动 + 混入深色',
    '    float jit = hash11(float(k) + 1.0);',
    '    sc = mix(deepCol, sc, 0.72 + 0.28 * jit);',
    '',
    '    float amt = line * face + halo * u_glow * 0.55;',
    '    // 渐进饱和累加：丝带中心处几十条丝的柔光会重叠在同一像素上，',
    '    // 若线性相加就变成一块亮度均匀的「平光板」，丢掉丝的独立感。',
    '    // 让已亮的像素增益自动变小 ⇒ 亮结仍是亮结，但细丝的层次被保住。',
    '    float head = 1.0 - clamp(core * 0.92, 0.0, 0.94);',
    '    col  += sc * amt * head;',
    '    core += amt * head;',
    '  }',
    '',
    '  // ---- 脉冲：沿丝传播的亮带 ----',
    '  float pulse = 0.0;',
    '  {',
    '    float travel = (u_ptime - u_pulseA.y) * u_pulseSpeed * 0.55;',
    '    float decay  = exp(-max(u_ptime - u_pulseA.y, 0.0) * 1.1);',
    '    float pos = u_pulseA.x + u_pulseA.w * travel;',
    '    float d1 = along - pos;',
    '    float d2 = along - (u_pulseA.x - travel);',
    '    float band1 = exp(-d1 * d1 / 0.0032);',
    '    float band2 = exp(-d2 * d2 / 0.0032);',
    '    float band = (u_pulseA.w < -0.5) ? max(band1, band2) : band1;',
    '    pulse += band * u_pulseA.z * decay;',
    '  }',
    '  {',
    '    float travel = (u_ptime - u_pulseB.y) * u_pulseSpeed * 0.55;',
    '    float decay  = exp(-max(u_ptime - u_pulseB.y, 0.0) * 1.1);',
    '    float pos = u_pulseB.x + u_pulseB.w * travel;',
    '    float d1 = along - pos;',
    '    float band1 = exp(-d1 * d1 / 0.0032);',
    '    pulse += band1 * u_pulseB.z * decay;',
    '  }',
    '  col *= (1.0 + pulse * u_pulseStrength);',
    '',
    '  // ---- 整体曝光 + hover 提亮 ----',
    '  float I = u_intensity * (1.0 + u_hover * u_hoverBoost * 0.35);',
    '  col *= I;',
    '',
    '  // ---- 保色相过曝压缩（软肩）----',
    '  // 越过拐点的部分做指数软肩：m→∞ 时增益→1.0，永远不到顶、保持单调、色相不变。',
    '  float m = max(max(col.r, col.g), col.b);',
    '  if (m > 0.72) {',
    '    float k = 0.72 + 0.28 * (1.0 - exp(-(m - 0.72) / 0.28));',
    '    col *= k / m;',
    '  }',
    '',
    '  // ---- 输出：glow 加光 / ink 上色 ----',
    '  vec3 outc;',
    '  if (u_glowMode > 0.5) {',
    '    outc = u_bg + col;',
    '  } else {',
    '    float cov = clamp(core * I * 1.6, 0.0, 1.0);',
    '    outc = mix(u_bg, col, cov);',
    '  }',
    '  gl_FragColor = vec4(clamp(outc, 0.0, 1.0), 1.0);',
    '}'
  ].join('\n');

  /**
   * 颜色解析 —— 刻意同时吃三种写法。
   * 为什么：Liquid 的 `color` 设置给出来的是 Color 对象，直接插值到 JSON 里时
   * 具体是 `#RRGGBB` 还是 `rgb(r, g, b)` 取决于输出上下文，不值得去猜。
   * 三种都认 ⇒ 后台换格式也不会静默变黑。
   */
  function hex2rgb(h) {
    var s = String(h).trim();
    var m = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
    if (m) return [+m[1] / 255, +m[2] / 255, +m[3] / 255];
    s = s.replace('#', '');
    if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    var n = parseInt(s.slice(0, 6), 16);
    if (!isFinite(n)) return [0, 0, 0];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  function lum(c) { return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; }

  function init(stage) {
    if (stage.dataset.npRibbonsReady === '1') return;
    stage.dataset.npRibbonsReady = '1';

    // ---- 参数：优先读 <script class="np-ribbons__cfg"> 里的 JSON ----
    var cfg = {};
    for (var k in DEFAULTS) cfg[k] = DEFAULTS[k];
    var rawEl = stage.querySelector('.np-ribbons__cfg');
    var rawTxt = rawEl ? rawEl.textContent.trim() : '';
    if (rawTxt) {
      var j = null;
      try {
        j = JSON.parse(rawTxt);
      } catch (e) {
        /* 🔴 容错：Liquid 的 `color` 设置插值时可能吐出**裸值**（`rgb(55, 201, 216)` 或 `#37C9D8`），
           未加引号就是非法 JSON ⇒ 整个 JSON.parse 失败、**所有参数一起回落默认值**，
           表现为「后台改了整块没反应」，极难查。
           这里给未加引号的颜色字面量补上引号再试一次。
           ⚠️ 判据是「前面那个字符不是引号」：`[:,\[\s]` 覆盖 `:rgb` / `[rgb` / `,#000000` /
           换行后的值；已被引号包住的（`"#37C9D8"`）不会命中。
           （当前 Liquid 模板里颜色本来就带引号，这层只是防御——别因为「跑得通」就删掉。） */
        try {
          j = JSON.parse(rawTxt.replace(
            /([:,\[\s])(rgba?\([^)]*\)|#[0-9a-fA-F]{3,8})/g,
            function (m, pre, val) { return pre + '"' + val + '"'; }
          ));
        } catch (e2) {
          j = null;
          if (window.console) console.warn('[np-ribbons] 配置 JSON 解析失败，回落到默认参数');
        }
      }
      if (j) for (var q in j) if (j[q] !== null && j[q] !== undefined && j[q] !== '') cfg[q] = j[q];
    }
    cfg.colors = (cfg.colors && cfg.colors.length >= 2) ? cfg.colors.slice(0, 2) : DEFAULTS.colors.slice();

    // 主题 CSS 变量可以让后台设置不必写进 JSON（例如 --np-rb-quality: 0.7）
    var cs = getComputedStyle(stage);
    var vq = parseFloat(cs.getPropertyValue('--np-rb-quality'));
    if (!isNaN(vq) && vq > 0) cfg.quality = Math.min(1, vq);

    var canvas = document.createElement('canvas');
    canvas.className = 'np-ribbons__cv';
    canvas.setAttribute('aria-hidden', 'true');
    stage.insertBefore(canvas, stage.firstChild);

    var gl = null, prog = null, U = {}, raf = 0, running = false;
    var animT = 0, pulseT = 0, lastTs = 0;
    var hoverAmt = 0, hoverTarget = 0;
    var pulses = [[0.5, -99, 0, 1], [0.5, -99, 0, 1]];
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function buildGL() {
      var opts = { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false };
      gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts);
      if (!gl) return false;
      function sh(type, src) {
        var s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
          if (window.console) console.warn('[np-ribbons] shader:', gl.getShaderInfoLog(s));
          return null;
        }
        return s;
      }
      var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return false;
      prog = gl.createProgram();
      gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        if (window.console) console.warn('[np-ribbons] link:', gl.getProgramInfoLog(prog));
        return false;
      }
      gl.useProgram(prog);
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      ['u_res','u_time','u_ptime','u_colA','u_colB','u_bg','u_glowMode','u_wires','u_lineWidth',
       'u_ribbonWidth','u_amplitude','u_waveLength','u_angle','u_twist','u_curl','u_depth',
       'u_stripes','u_colorFlow','u_glow','u_intensity','u_speed','u_spin',
       'u_pulseSpeed','u_pulseStrength','u_hover','u_hoverBoost','u_pulseA','u_pulseB'
      ].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
      return true;
    }

    function resize() {
      if (!gl) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2) * cfg.quality;
      var w = Math.max(1, Math.floor(stage.clientWidth * dpr));
      var h = Math.max(1, Math.floor(stage.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    function fire(x, dir, strength) {
      pulses[0] = pulses[1];
      pulses[1] = [x, pulseT, strength, dir];
    }

    function draw() {
      if (!gl) return;
      resize();
      var cA = hex2rgb(cfg.colors[0]), cB = hex2rgb(cfg.colors[1]), bg = hex2rgb(cfg.backgroundColor);
      var glowMode = 1;
      if (cfg.mode === 'ink') glowMode = 0;
      else if (cfg.mode === 'auto') glowMode = lum(bg) > 0.42 ? 0 : 1;

      gl.uniform2f(U.u_res, canvas.width, canvas.height);
      gl.uniform1f(U.u_time, animT);
      gl.uniform1f(U.u_ptime, pulseT);
      gl.uniform3fv(U.u_colA, cA);
      gl.uniform3fv(U.u_colB, cB);
      gl.uniform3fv(U.u_bg, bg);
      gl.uniform1f(U.u_glowMode, glowMode);
      gl.uniform1f(U.u_wires, cfg.wires);
      gl.uniform1f(U.u_lineWidth, cfg.lineWidth);
      gl.uniform1f(U.u_ribbonWidth, cfg.ribbonWidth);
      gl.uniform1f(U.u_amplitude, cfg.amplitude);
      gl.uniform1f(U.u_waveLength, cfg.waveLength);
      gl.uniform1f(U.u_angle, cfg.angle);
      gl.uniform1f(U.u_twist, cfg.twist);
      gl.uniform1f(U.u_curl, cfg.curl);
      gl.uniform1f(U.u_depth, cfg.depth);
      gl.uniform1f(U.u_stripes, cfg.stripes);
      gl.uniform1f(U.u_colorFlow, cfg.colorFlow);
      gl.uniform1f(U.u_glow, cfg.glow);
      gl.uniform1f(U.u_intensity, cfg.intensity);
      gl.uniform1f(U.u_speed, cfg.speed);
      gl.uniform1f(U.u_spin, cfg.spin);
      gl.uniform1f(U.u_pulseSpeed, cfg.pulseSpeed);
      gl.uniform1f(U.u_pulseStrength, cfg.pulseStrength);
      gl.uniform1f(U.u_hover, hoverAmt);
      gl.uniform1f(U.u_hoverBoost, cfg.hoverBoost);
      gl.uniform4fv(U.u_pulseA, pulses[0]);
      gl.uniform4fv(U.u_pulseB, pulses[1]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function loop(ts) {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      if (!lastTs) lastTs = ts;
      var dt = Math.min((ts - lastTs) / 1000, 0.05);
      lastTs = ts;
      var eff = cfg.speed * (1 + hoverAmt * cfg.hoverBoost * 0.5);
      // paused / 减少动效：动画冻结，脉冲照跑（脉冲是偶发的，不构成持续动效）
      if (!cfg.paused && !reduceMotion) animT += dt * eff;
      else if (cfg.paused && cfg.interactive) { /* 冻结 */ }
      pulseT += dt;
      hoverAmt += (hoverTarget - hoverAmt) * Math.min(dt * 6, 1);
      draw();
    }

    function start() {
      if (running) return;
      running = true;
      lastTs = 0;
      raf = requestAnimationFrame(loop);
    }
    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    // ---- 交互 ----
    function localAlong(clientX, clientY) {
      var r = stage.getBoundingClientRect();
      var asp = r.width / Math.max(r.height, 1);
      var px = ((clientX - r.left) / r.width - 0.5) * asp;
      var py = (0.5 - (clientY - r.top) / r.height);
      var a = cfg.angle * Math.PI / 180;
      return px * Math.cos(a) + py * Math.sin(a);
    }
    stage.addEventListener('pointermove', function (e) {
      if (!cfg.interactive) return;
      hoverTarget = 1;
      if (cfg.hoverPulses) {
        var x = localAlong(e.clientX, e.clientY);
        if (Math.abs(x - pulses[1][0]) > 0.10) fire(x, 1, 0.85);
      }
    }, { passive: true });
    stage.addEventListener('pointerleave', function () { hoverTarget = 0; }, { passive: true });
    stage.addEventListener('pointerdown', function (e) {
      if (!cfg.interactive || !cfg.clickPulse) return;
      fire(localAlong(e.clientX, e.clientY), -1, 1.3);
    }, { passive: true });

    window.addEventListener('resize', function () { if (running) resize(); }, { passive: true });

    if (!buildGL()) {
      // 拿不到 WebGL ⇒ 整层不输出（纯装饰，安全退化）
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      stage.dataset.npRibbonsReady = 'gone';
      return;
    }
    stage.dataset.npRibbonsReady = '1';

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { en.isIntersecting ? start() : stop(); });
      }, { rootMargin: '120px' }).observe(stage);
    } else {
      start();
    }
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });
  }

  function boot() {
    document.querySelectorAll('[data-np-ribbons]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify 主题编辑器：区块被重新渲染时需要重新初始化
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);
})();
